import { pipe } from 'ramda';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { map, ofNullable, ifEmpty, orElse } from 'core/maybe';
import { logger } from 'services/logger';
import { useLatestRef } from './use-latest';
const defaultSerializer = {
    deserialize: map(JSON.parse),
    serialize: (value)=>JSON.stringify(value)
};
export const useLocalStorage = (key, otherwise, { deserialize, serialize } = defaultSerializer)=>{
    const oldKeyRef = useRef(key);
    const refFree = useLatestRef({
        deserialize,
        otherwise,
        serialize
    });
    const getter = useMemo(()=>pipe(()=>window.localStorage.getItem(key), ofNullable, refFree.current.deserialize, ifEmpty(()=>window.localStorage.setItem(key, refFree.current.serialize(refFree.current.otherwise))), orElse(refFree.current.otherwise)), [
        key,
        refFree
    ]);
    const [value, setValue] = useState(getter);
    useEffect(()=>{
        if (oldKeyRef.current !== key) {
            setValue(getter());
            oldKeyRef.current = key;
        }
    }, [
        getter,
        key
    ]);
    // Store current values in refs to prevent listener recreation on every value change
    const valueRef = useRef(value);
    const setValueRef = useRef(setValue);
    useEffect(()=>{
        valueRef.current = value;
        return ()=>{
            // Help GC by clearing ref on value change
            valueRef.current = otherwise;
        };
    }, [
        value,
        otherwise
    ]);
    useEffect(()=>{
        setValueRef.current = setValue;
        return ()=>{
            // Clear function ref to prevent retention
            setValueRef.current = ()=>{};
        };
    }, [
        setValue
    ]);
    const listener = useCallback((e)=>{
        if (e.storageArea !== localStorage || e.key !== key) {
            return;
        }
        const next = pipe(()=>e.newValue, ofNullable, deserialize, orElse(otherwise))();
        // Use ref to get current value without adding to dependencies
        if (serialize(valueRef.current) !== serialize(next)) {
            setValueRef.current(next);
        }
    }, [
        key,
        otherwise,
        deserialize,
        serialize
    ]);
    useEffect(()=>{
        window.addEventListener('storage', listener);
        return ()=>{
            window.removeEventListener('storage', listener);
        };
    }, [
        listener
    ]);
    useEffect(()=>{
        const newValue = serialize(value);
        window.localStorage.setItem(key, newValue);
        const init = {
            key,
            newValue
        };
        if (window.localStorage instanceof Storage) {
            init.storageArea = window.localStorage;
        }
        try {
            window.dispatchEvent(new StorageEvent('storage', init));
        } catch (err) {
            logger.warn('Failed to dispatch synthetic storage event', {
                error: err
            });
        }
    }, [
        key,
        serialize,
        value
    ]);
    return [
        value,
        setValue
    ];
};
